import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import LogoLight from '../assets/Logolight.png';
import HelpCircleMap from '../assets/li_help-circle-map.svg';
import HelpCircle from '../assets/li_help-circle.svg';
import SpectreLogo from '../assets/spectre-logo.png';
import { useMainContext } from '../context/MainContext.tsx';
import { useMergedConfig } from '../context/MergedConfigProvider.tsx';
import { useScreenNavigation } from '../hooks/useScreenNavigation.ts';
import { nonSpectreJourney } from '../utils/backgroundImage.ts';
import BackButton from './BackButton.tsx';
import Button from './Button.tsx';
import FormHeader from './FormHeader.tsx';
import ProgressPill from './ProgressPill.tsx';

const RESPONSIVE_PADDING = 'xxxs:px-6 xs:px-10 sm:px-28 md:px-6 l1g:px-14 xl:p-40 3xl:px-48';

type HeaderProps = {
  headerBackBtnVisible?: boolean;
  headerBackBtnOnClick?: () => void;
  headerBackBtnOnPointerDown?: () => void;
  headerBackBtnOnPointerUp?: () => void;
  headerBackBtnOnPointerCancel?: () => void;
  headerBackBtnDisabled?: boolean;
  headerStepperVisible?: boolean;
  journeyId?: string;
  headerStepperCurrentIndex?: number;
  headerStepperTotalSteps?: number;
  headerIcon?: string;
};
const Header: FC<HeaderProps> = ({
  headerBackBtnVisible = false,
  headerBackBtnOnClick,
  headerBackBtnOnPointerDown,
  headerBackBtnOnPointerUp,
  headerBackBtnOnPointerCancel,
  headerBackBtnDisabled,
  headerStepperVisible = false,
  headerStepperCurrentIndex = 0,
  journeyId,
  headerStepperTotalSteps = 0,
  headerIcon,
}) => {
  const Logo = nonSpectreJourney(journeyId) ? LogoLight : SpectreLogo;
  const logoStyles = nonSpectreJourney(journeyId)
    ? { width: '38px', height: '43px' }
    : { width: '284px', height: '45px' };
  return (
    <div id="header">
      <div className="logo flex justify-center mb-8">
        <img style={logoStyles} src={Logo} alt="CMC Logo" />
      </div>

      {headerIcon && (
        <div className="mx-auto bg-intent-success-5 rounded-full w-[100px] h-[100px] flex items-center justify-center mt-12">
          <img src={headerIcon} alt="Success" className="w-14 h-14" />
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <BackButton
          journeyId={journeyId}
          hide={!headerBackBtnVisible}
          onClick={headerBackBtnOnClick}
          onPointerDown={headerBackBtnOnPointerDown}
          onPointerUp={headerBackBtnOnPointerUp}
          onPointerCancel={headerBackBtnOnPointerCancel}
          disabled={headerBackBtnDisabled}
        />
        {headerStepperVisible && (
          <ProgressPill step={headerStepperCurrentIndex + 1} totalSteps={headerStepperTotalSteps} />
        )}
      </div>
    </div>
  );
};

type FooterProps = {
  footerBtnLabel: string;
  footerBtnOnClick: () => void;
  footerBtnLoading: boolean;
  footerBtnDisabled: boolean;
  showFinishButton?: boolean;
  journeyId?: string;
  helpBtnOnClick?: () => void;
  isThankYou?: boolean;
};
const Footer: FC<FooterProps> = ({
  footerBtnLabel,
  footerBtnOnClick,
  showFinishButton,
  footerBtnLoading,
  footerBtnDisabled,
  journeyId,
  helpBtnOnClick,
  isThankYou = false,
}) => {
  const Help = nonSpectreJourney(journeyId) ? HelpCircleMap : HelpCircle;
  const { embedded } = useMergedConfig();
  const footerClasses = twMerge(
    RESPONSIVE_PADDING,
    'border-t border-surface-separator !pt-6',
    embedded ? '!pb-6' : '!pb-12'
  );
  return (
    <div id="footer" className={footerClasses}>
      <div className={isThankYou ? 'w-full' : 'flex flex-row items-center justify-start gap-4 align-baseline'}>
        {!isThankYou && helpBtnOnClick && (
          <Button
            key={'help'}
            loading={false}
            variant="secondary"
            className="w-[52px] h-[52px] m-0"
            onClick={helpBtnOnClick}
          >
            <img src={Help} alt="Help Icon" width="52" height="52" />
          </Button>
        )}

        <div className={isThankYou ? 'w-full' : 'flex-1'}>
          <Button
            loading={footerBtnLoading}
            disabled={footerBtnDisabled}
            variant="primary"
            className={isThankYou ? 'm-0 w-full rounded-full py-4' : 'm-0'}
            onClick={footerBtnOnClick}
          >
            {footerBtnLabel || (showFinishButton ? 'Finish' : 'Continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};

type BaseScreenProps = HeaderProps & {
  formTitle: string;
  formSubtitle?: string;
  applicationSubmitted?: boolean;
  children?: ReactNode;
  headerIcon?: string;
  isThankYou?: boolean;
  hideFooter?: boolean;
  hideFormTitle?: boolean;
} & Omit<FooterProps, 'helpBtnOnClick'>;
export const BaseScreen: FC<BaseScreenProps> = ({
  headerBackBtnVisible,
  headerBackBtnOnClick,
  headerBackBtnOnPointerDown,
  headerBackBtnOnPointerUp,
  headerBackBtnOnPointerCancel,
  headerBackBtnDisabled,
  headerStepperVisible,
  headerStepperCurrentIndex,
  headerStepperTotalSteps,
  formTitle,
  formSubtitle,
  applicationSubmitted,
  footerBtnLabel,
  footerBtnOnClick,
  footerBtnLoading,
  footerBtnDisabled,
  headerIcon,
  children,
  isThankYou = false,
  hideFooter = false,
  hideFormTitle = false,
}) => {
  const { setIsHelpDrawerOpen, journeyId } = useMainContext();
  const { embedded, product } = useMergedConfig();

  useScreenNavigation(formTitle, footerBtnOnClick, footerBtnDisabled, footerBtnLoading);

  const showFinishButton = product === 'CFD' && applicationSubmitted;
  const mainClasses = twMerge(
    RESPONSIVE_PADDING,
    'flex flex-col flex-1 md:pb-0 overflow-y-auto overscroll-contain [@supports(-webkit-overflow-scrolling:touch)]:[-webkit-overflow-scrolling:touch]',
    hideFooter && 'main-without-footer',
    isThankYou ? 'min-h-screen md:min-h-[unset] md:h-screen text-center' : '',
    embedded ? '!pt-6' : '!pt-10'
  );

  return (
    <>
      <div id="main" className={mainClasses}>
        {!embedded && (
          <Header
            journeyId={journeyId}
            headerBackBtnVisible={headerBackBtnVisible}
            headerBackBtnOnClick={headerBackBtnOnClick}
            headerBackBtnOnPointerDown={headerBackBtnOnPointerDown}
            headerBackBtnOnPointerUp={headerBackBtnOnPointerUp}
            headerBackBtnOnPointerCancel={headerBackBtnOnPointerCancel}
            headerBackBtnDisabled={headerBackBtnDisabled}
            headerStepperVisible={headerStepperVisible}
            headerStepperCurrentIndex={headerStepperCurrentIndex}
            headerStepperTotalSteps={headerStepperTotalSteps}
            headerIcon={headerIcon}
          />
        )}
        {embedded && !isThankYou && (
          <div className="flex justify-between items-center mb-4">
            <BackButton
              journeyId={journeyId}
              hide={!headerBackBtnVisible}
              onClick={headerBackBtnOnClick}
              onPointerDown={headerBackBtnOnPointerDown}
              onPointerUp={headerBackBtnOnPointerUp}
              onPointerCancel={headerBackBtnOnPointerCancel}
              disabled={headerBackBtnDisabled}
            />
          </div>
        )}
        <div className="flex flex-col">
          {formTitle && !hideFormTitle && <FormHeader title={formTitle} subtitle={formSubtitle} />}
          <div className="form-container">{children}</div>
        </div>
      </div>
      {!hideFooter && (
        <Footer
          journeyId={journeyId}
          helpBtnOnClick={() => setIsHelpDrawerOpen(true)}
          showFinishButton={showFinishButton}
          isThankYou={isThankYou}
          footerBtnLabel={footerBtnLabel}
          footerBtnOnClick={footerBtnOnClick}
          footerBtnLoading={footerBtnLoading}
          footerBtnDisabled={footerBtnDisabled}
        />
      )}
    </>
  );
};
